/**
 * TableExportMenu — a small inline "Export table" dropdown rendered right
 * below any markdown table in an Open Analysis chat reply. Reads the cell
 * text straight off the already-rendered DOM table (via tableRef), so it
 * works for ANY table the AI happens to respond with — no markdown parsing
 * needed. Mirrors the Export CSV/Excel pattern already used for Drawing
 * Comparison exports (components/compare/ComparisonTool.jsx).
 */
import { useState } from "react";
import { DownloadSimple, FileCsv, FileXls, CaretDown, Spinner } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../ui/dropdown-menu";
import { toast } from "sonner";
import { exportAnalysisTable } from "../../api";

function extractRowsFromTable(tableEl) {
  if (!tableEl) return [];
  return Array.from(tableEl.querySelectorAll("tr"))
    .map((row) => Array.from(row.querySelectorAll("th,td")).map((cell) => cell.innerText.trim()))
    .filter((row) => row.length > 0);
}

export default function TableExportMenu({ tableRef, filenamePrefix = "analysis_table" }) {
  const [exportingFormat, setExportingFormat] = useState(null); // "csv" | "excel" | null

  const handleExport = async (format) => {
    const tableEl = tableRef.current?.querySelector("table");
    const rows = extractRowsFromTable(tableEl);
    if (rows.length === 0) {
      toast.error("Nothing to export — this table looks empty.");
      return;
    }
    setExportingFormat(format);
    try {
      const res = await exportAnalysisTable(rows, format, filenamePrefix);
      const mime = format === "csv"
        ? "text/csv"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([res.data], { type: mime });
      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/);
      const fallbackName = `${filenamePrefix}.${format === "csv" ? "csv" : "xlsx"}`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Table exported as ${format === "csv" ? "CSV" : "Excel"}`);
    } catch (e) {
      let message = e.message;
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          message = JSON.parse(text)?.detail || message;
        } catch (_) { /* keep default message */ }
      } else {
        message = e.response?.data?.detail || message;
      }
      toast.error("Export failed: " + message);
    } finally {
      setExportingFormat(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          disabled={!!exportingFormat}
          className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-primary
                     border border-slate-200 hover:border-primary/40 bg-white rounded-md px-2.5 py-1 transition-colors disabled:opacity-50"
          data-testid="table-export-button"
        >
          {exportingFormat
            ? <><Spinner size={12} className="animate-spin" /> Exporting...</>
            : <><DownloadSimple size={12} /> Export table <CaretDown size={10} /></>
          }
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem
          onClick={() => handleExport("excel")}
          disabled={!!exportingFormat}
          data-testid="table-export-excel-option"
        >
          <FileXls size={16} className="text-green-600" />
          Export as Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport("csv")}
          disabled={!!exportingFormat}
          data-testid="table-export-csv-option"
        >
          <FileCsv size={16} className="text-slate-500" />
          Export as CSV
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
